import { transferSearchListVariants } from 'Modules/softphone/components/SoftphoneUI/index.styles';
import React, { useCallback } from 'react';
import { Skeleton } from '~/lib/ui/skeleton';
import { SKELETON_ITEMS_COUNT } from './index.constants';
import { TransferSearchListProps } from './index.types';
import { TransferDirectDialOption } from './TransferDirectDialOption';
import { TransferSearchEmptyState } from './TransferSearchEmptyState';
import { TransferSearchListItem } from './TransferSearchListItem';

/**
 * Renders a list of contacts based on the search term.
 * Shows a loading skeleton while fetching results.
 * Shows an empty state when no results are found.
 */
export const TransferSearchList = ({
  results,
  isLoading,
  showDirectNumberOption,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  searchTerm,
  onSelectContact,
  onSelectDirectNumber,
  variant = 'popup',
  testID,
}: TransferSearchListProps): React.ReactElement => {
  const componentTestId = testID ? `${testID}-results` : 'results';
  const trimmedQuery = searchTerm.trim();
  const showEmptyState =
    !isLoading && results.length === 0 && trimmedQuery.length > 0;

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 100;
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  return (
    <div
      className={transferSearchListVariants({ variant })}
      onScroll={handleScroll}
      data-testid={componentTestId}
      role="list"
      aria-label="Transfer contact results"
    >
      {/* Direct dial option */}
      {showDirectNumberOption && (
        <TransferDirectDialOption
          onSelectDirectNumber={onSelectDirectNumber}
          searchTerm={searchTerm}
          testID={testID}
        />
      )}

      {/* Loading state */}
      {isLoading && (
        <div
          className="flex flex-col gap-[8px] px-[16px] py-[4px]"
          data-testid={`${componentTestId}-loading`}
          role="status"
          aria-label="Loading contacts"
        >
          {Array.from({ length: SKELETON_ITEMS_COUNT }).map((_, i) => (
            <div key={i} className="flex items-center gap-[10px] py-[8px]">
              <Skeleton className="size-[32px] rounded-full flex-shrink-0" />
              <div className="flex flex-col gap-[4px] flex-1">
                <Skeleton className="h-[14px] w-[120px] rounded" />
                <Skeleton className="h-[12px] w-[80px] rounded" />
                {trimmedQuery.length > 0 && (
                  <Skeleton className="h-[12px] w-[100px] rounded" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All contacts section title */}
      {!isLoading && trimmedQuery.length > 0 && (
        <h4
          className="text-[16px] font-semibold text-foreground tracking-wide px-[16px] py-[6px]"
          data-testid={`${componentTestId}-contacts-title`}
        >
          All contacts
        </h4>
      )}

      {/* Contact results */}
      {!isLoading &&
        results.map((contact) => (
          <TransferSearchListItem
            key={contact.id}
            contact={contact}
            searchTerm={searchTerm}
            onSelectContact={onSelectContact}
            testID={testID}
          />
        ))}

      {/* Empty state */}
      {showEmptyState && (
        <TransferSearchEmptyState
          showDirectNumberOption={showDirectNumberOption}
          testID={testID}
        />
      )}

      {/* Fetching next page indicator */}
      {isFetchingNextPage && (
        <div
          className="flex flex-col gap-[8px] px-[8px] py-[4px]"
          data-testid={`${componentTestId}-loading-more`}
          role="status"
          aria-label="Loading more contacts"
        >
          <div className="flex items-center gap-[10px] py-[10px]">
            <Skeleton className="size-[32px] rounded-full flex-shrink-0" />
            <div className="flex flex-col gap-[4px] flex-1">
              <Skeleton className="h-[14px] w-[120px] rounded" />
              <Skeleton className="h-[12px] w-[80px] rounded" />
              {trimmedQuery.length > 0 && (
                <Skeleton className="h-[12px] w-[100px] rounded" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TransferSearchList.displayName = 'TransferSearchList';
